import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core'
import { IFolder } from 'src/folder/types'
import { dayjs } from 'src/lib/dayjs'
import { Menu } from 'src/ui/menu/menu'
import { classes, colorClasses } from 'src/utils/css'

import { ICall } from '../../call/types'
import { IUser } from '../../page/admin/types'
import { ColorsType, FontWeightType } from '../../tailwind'
import { EditableText } from '../../ui/editable-text/functional'

@Component({
  tag: 'video-call-chip',
})
export class CallChip {
  @Prop() call: ICall
  @Prop() currentUser: IUser
  @Prop() titleWeight: FontWeightType = 'bold'

  @Event() addedToFolder: EventEmitter<IFolder>
  @Event() failedAddToFolder: EventEmitter<IFolder>
  @Event() removeFromFolder: EventEmitter<IFolder>
  @Event() titleFocused: EventEmitter
  @Event() titleEdited: EventEmitter<string>

  @Prop() date = false
  @Prop() rating = false
  @Prop() avatar = false
  @Prop() icon = false
  @Prop() large = false
  @Prop() companyName = true
  @Prop() folders = false

  @Prop() dateColor: ColorsType

  @State() showingIcon = false

  menuEl: HTMLUiTooltipElement

  componentWillLoad() {
    this.showingIcon = this.icon
  }

  render() {
    return (
      <Host class='flex overflow-hidden items-center'>
        {this.avatar && !this.call.internal && (this.call.company?.icon_url || this.call.company?.name) && (
          <call-avatar style={{ fontSize: '2.3em', marginRight: '.3em' }} call={this.call} />
        )}
        <div
          class={`flex flex-col flex-grow overflow-hidden font-${this.titleWeight} text-glyph-base`}
          style={{ lineHeight: '1.3' }}
        >
          {this.currentUser &&
          ((this.currentUser.team && this.currentUser.team.isAdmin) || this.currentUser.id === this.call.host.id) ? (
            <div class='flex flex-row spacing-x-2'>
              <EditableText
                hideIcon={this.icon}
                onFocus={() => {
                  this.showingIcon = false
                  this.titleFocused.emit()
                }}
                onBlur={() => {
                  this.showingIcon = this.icon
                }}
                onChange={(e) => this.titleEdited.emit((e.target as Element).textContent)}
                value={this.call.title}
                style={{ fontSize: this.large ? '20px' : '1em' }}
              />
              {this.showingIcon && <ui-icon icon='pencil-square' class='pt-1 text-surface-highest' />}
            </div>
          ) : (
            <span style={{ fontSize: this.large ? '20px' : '1em' }}>{this.call.title}</span>
          )}
          <div
            class={`flex flex-wrap -mt-4px spacing-x-${
              !this.call.folders || this.call.folders.length === 0 ? '12px' : '8px'
            }`}
          >
            {this.date && this.call.started_at && (
              <div
                class={`mt-4px text-small font-medium leading-19px ${colorClasses({
                  text: this.dateColor || 'glyph-base',
                })}`}
              >
                {this.companyName && !!this.call.company && !!this.call.company.name
                  ? `${this.call.company.name} - `
                  : ''}
                {dayjs(this.call.started_at).format('MMM D, YYYY')}
              </div>
            )}
            {this.rating && this.call.star_rating && (
              <ui-stars
                style={{ fontSize: '0.9em' }}
                class='items-center'
                disabled
                total={this.call.star_rating}
                value={this.call.star_rating}
              />
            )}
            {this.folders && (
              <Fragment>
                {this.call.folders.map((folder) => {
                  return (
                    <div class='flex flex-shrink-0 items-center mt-4px text-small font-medium leading-19px text-brand-base spacing-x-1px'>
                      <a
                        class={`flex items-center pl-6px h-16px hover:text-surface-base bg-brand-base bg-opacity-10 hover:bg-opacity-100 ${classes(
                          !!folder.actions.removeUrl,
                          'rounded-l-full pr-4px',
                          'rounded-full pr-6px'
                        )} spacing-x-3px`}
                        href={folder.actions.showUrl}
                      >
                        <ui-icon icon='folder' style={{ fontSize: '12px' }} />
                        <span>{folder.name}</span>
                      </a>
                      {!!folder.actions.removeUrl && (
                        <button
                          class='flex items-center w-16px h-16px text-small font-medium leading-19px hover:text-surface-base bg-brand-base bg-opacity-10 hover:bg-opacity-100 rounded-r-full opacity-90'
                          onClick={() => {
                            this.removeFromFolder.emit(folder)
                          }}
                        >
                          <ui-icon icon='x' style={{ fontSize: '14px' }} />
                        </button>
                      )}
                    </div>
                  )
                })}
                {!!this.call.folderActions.createUrl && !!this.call.folderActions.indexUrl && (
                  <button class='flex items-center mt-4px text-small font-medium leading-19px text-glyph-lighter spacing-x-3px'>
                    <ui-icon icon='folder-plus' style={{ fontSize: '12px' }} />
                    <span>{this.call.folders.length === 0 ? 'Add to Folder' : 'Add'}</span>
                    <Menu ref={(el) => (this.menuEl = el)} allowClick={false} contentWidth='48'>
                      <add-to-folder-menu
                        call={this.call}
                        createUrl={this.call.folderActions.createUrl}
                        indexUrl={this.call.folderActions.indexUrl}
                        onAdded={(e) => {
                          this.addedToFolder.emit(e.detail)
                          this.menuEl?.close()
                        }}
                        onFailed={(e) => {
                          this.failedAddToFolder.emit(e?.detail)
                          this.menuEl?.close()
                        }}
                      />
                    </Menu>
                  </button>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </Host>
    )
  }
}
